<script setup lang="ts">
    import { inject, reactive } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { getCurrentClientCompany, ClientCompany } from './AccountBillingDetails.access';
    import Loading from '@shared/components/Loading.vue';
    import Modal from '@shared/components/Modal.vue';
    import List from '@shared/components/List/List.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import { formatDate } from '@shared/utils/formatDateTime';
    import { formatNumericText } from '@shared/utils/formatNumericText';
    import { Order, getOrders, getOrder } from './AccountTransactions.access';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const state = reactive<{
        isLoading: boolean;
        loadError: boolean;
        orders: Order[];
        clientCompany: ClientCompany | null;
        selectedOrder: Order | null;
        isLoadingOrder: boolean;
        showModal: boolean;
    }>({
        isLoading: true,
        loadError: false,
        orders: [],
        clientCompany: null,
        selectedOrder: null,
        isLoadingOrder: false,
        showModal: false,
    });

    (async () => {
        try {
            const [orders, clientCompany] = await Promise.all([getOrders(), getCurrentClientCompany()]);

            state.orders = orders.filter((order) => order.TypeName !== 'GiveAway' && order.Amount > 0);
            state.clientCompany = clientCompany;
        } catch {
            state.loadError = true;
        } finally {
            state.isLoading = false;
        }
    })();

    const filterKeys = ['OrderNumber'];

    const orderOptions = [
        { key: 'Created', display: $Resources.OrderCreatedLabel },
        { key: 'Amount', display: $Resources.AmountLabel },
        { key: 'StatusName', display: $Resources.OrderStatusLabel },
    ];

    function orderTypeLabel(order: Order): string {
        return ($Resources as any)['OrderType' + order.TypeName] || order.TypeName;
    }

    function orderStatusLabel(order: Order): string {
        return ($Resources as any)['OrderStatus' + order.StatusName] || order.StatusName;
    }

    function invoiceHeading(order: Order): string {
        return $Resources.InvoiceHeading.replace('{{OrderType}}', orderTypeLabel(order));
    }

    async function select(order: Order): Promise<void> {
        state.selectedOrder = null;
        state.showModal = true;
        state.isLoadingOrder = true;

        state.selectedOrder = await getOrder(order.OrderId).finally(() => (state.isLoadingOrder = false));
    }

    function modalClosed(): void {
        state.showModal = false;
    }

    function print(): void {
        window.print();
    }
</script>

<template>
    <h1 class="h2 m-lg-b">{{ $Resources.TransactionsHeading }}</h1>

    <Loading :show="state.isLoading" loadingStyle="progress-bar" fixed></Loading>

    <BsAlert v-if="state.loadError" :type="AlertTypes.Danger" :dismissible="false" :title="$Resources.ErrorHeading" :content="$Resources.ErrorContactSupport" />

    <List
        v-if="!state.isLoading && !state.loadError"
        itemId="OrderId"
        displayAs="table"
        :items="state.orders"
        :filterKeys="filterKeys"
        :orderOptions="orderOptions"
        orderByValue="Created"
        orderByDirection="desc"
        :tableHeaders="[
            $Resources.OrderCreatedLabel,
            $Resources.InvoiceLabel,
            $Resources.OrderStatusLabel,
            { name: $Resources.AmountLabel, classes: 'text-right' },
            $Resources.ShowLabel,
        ]">
        <template #item="{ item: order }">
            <td>{{ formatDate(order.Created) }}</td>
            <td>{{ orderTypeLabel(order) }}</td>
            <td>
                <i
                    class="fa"
                    aria-hidden="true"
                    :class="{
                        'fa-square c-success m-xs-r': order.StatusName === 'Paid',
                        'fa-square c-danger m-xs-r': order.StatusName === 'Unpaid' || order.StatusName === 'Failed',
                        'fa-square c-warning m-xs-r': order.StatusName === 'Waiting',
                    }"></i>
                {{ orderStatusLabel(order) }}
            </td>
            <td class="text-right">{{ formatNumericText(order.Amount, { precision: 2, suffix: ' ' + order.Currency.CurrencyCode }) }}</td>
            <td class="text-right">
                <button type="button" class="btn btn-default btn-xs" @click.prevent="select(order)">{{ $Resources.ShowLabel }}</button>
            </td>
        </template>
    </List>

    <Modal size="lg" :title="$Resources.InvoiceLabel" :show="state.showModal" @close="modalClosed">
        <template #content>
            <Loading :show="state.isLoadingOrder"></Loading>

            <div v-if="state.selectedOrder" id="receipt-print-area">
                <div class="invoice m-auto-r m-auto-l">
                    <div class="row m-xl-b">
                        <div class="col-md-6 col-print-6">
                            <div class="invoice-logo m-sm-b">
                                <img class="img-responsive" :src="'/content/img/Ungapped-logo-md-turquoise-dark.svg'" alt="Ungapped logo" />
                            </div>
                        </div>
                        <div class="col-md-6 col-print-6">
                            <p>
                                <small v-html="$Resources.InvoiceAddress"></small>
                            </p>
                        </div>
                    </div>

                    <hr />

                    <h1 class="h3 m-lg-b">{{ invoiceHeading(state.selectedOrder) }}</h1>

                    <div class="row m-lg-b">
                        <div class="col-md-6">
                            <h2 class="h4">{{ $Resources.CustomerLabel }}</h2>
                            <p>
                                {{ contextAccess.state.currentAccount?.ClientCompany?.Name }}
                                <br />
                                <span v-if="state.clientCompany?.BillingAddress" style="white-space: pre-line">{{ state.clientCompany.BillingAddress }}</span>
                            </p>
                        </div>
                        <div class="col-md-6">
                            <h2 class="h4">{{ $Resources.InvoiceLabel }}</h2>
                            <p>
                                {{ $Resources.OrderNumberLabel }}: {{ state.selectedOrder.OrderNumber }}
                                <br />
                                {{ $Resources.OrderStatusLabel }}:
                                <span
                                    class="strong"
                                    :class="{
                                        'c-success': state.selectedOrder.StatusName === 'Paid',
                                        'c-danger': state.selectedOrder.StatusName === 'Unpaid',
                                    }">
                                    {{ orderStatusLabel(state.selectedOrder) }}
                                </span>
                                <br />
                                {{ $Resources.OrderCreatedLabel }}: {{ formatDate(state.selectedOrder.Created) }}
                            </p>
                        </div>
                    </div>

                    <div class="table-responsive m-0-t m-lg-b">
                        <ul class="list text-lg fw-600 m-0-all text-nowrap b-0-t">
                            <li class="list-row" v-if="state.selectedOrder.ServiceLevelPrice">
                                <div class="list-cell">{{ $Resources.ServiceSubscriptionLabel }}</div>
                                <div class="list-cell text-right">
                                    {{ state.selectedOrder.ServiceLevelPrice.ServiceSubscriptionTier.Name }}
                                    {{ state.selectedOrder.ServiceLevelPrice.ServiceLevel.Name }}
                                </div>
                            </li>
                            <li class="list-row">
                                <div class="list-cell text-md p-sm-t p-sm-b normal">{{ $Resources.TotalBeforeVatLabel }}</div>
                                <div class="list-cell text-md p-sm-t p-sm-b normal text-right">
                                    {{
                                        formatNumericText(state.selectedOrder.Amount - state.selectedOrder.Vat, {
                                            precision: 2,
                                            suffix: ' ' + state.selectedOrder.Currency.CurrencyCode,
                                        })
                                    }}
                                </div>
                            </li>
                            <li class="list-row">
                                <div class="list-cell text-md p-sm-t p-sm-b normal">{{ $Resources.VatLabel }}</div>
                                <div class="list-cell text-md p-sm-t p-sm-b normal text-right">
                                    {{
                                        formatNumericText(state.selectedOrder.Vat, {
                                            precision: 2,
                                            suffix: ' ' + state.selectedOrder.Currency.CurrencyCode,
                                        })
                                    }}
                                </div>
                            </li>
                            <li class="list-row list-row-summary">
                                <div class="list-cell b-0-b">{{ $Resources.OrderTotalLabel }}</div>
                                <div class="list-cell b-0-b text-right">
                                    {{
                                        formatNumericText(state.selectedOrder.Amount, {
                                            precision: 2,
                                            suffix: ' ' + state.selectedOrder.Currency.CurrencyCode,
                                        })
                                    }}
                                </div>
                            </li>
                        </ul>
                    </div>

                    <template v-if="state.selectedOrder.Payments && state.selectedOrder.Payments.length > 0">
                        <h4>{{ $Resources.PaymentHeading }}</h4>

                        <div class="table-responsive m-0-t m-lg-b">
                            <ul class="list text-lg fw-600 m-0-all text-nowrap b-0-t" v-for="payment in state.selectedOrder.Payments" :key="payment.Created">
                                <li class="list-row">
                                    <div class="list-cell text-md p-sm-t p-sm-b normal">{{ $Resources.PaymentDateLabel }}</div>
                                    <div class="list-cell text-md p-sm-t p-sm-b normal text-right">{{ formatDate(payment.Created) }}</div>
                                </li>
                                <li class="list-row">
                                    <div class="list-cell text-md p-sm-t p-sm-b normal">{{ $Resources.AmountPaidLabel }}</div>
                                    <div class="list-cell text-md p-sm-t p-sm-b normal text-right">
                                        {{ formatNumericText(payment.Amount, { precision: 2, suffix: ' ' + payment.Currency.CurrencyCode }) }}
                                    </div>
                                </li>
                                <li class="list-row" v-if="payment.CreditCard">
                                    <div class="list-cell text-md p-sm-t p-sm-b normal">{{ $Resources.CardUsedLabel }}</div>
                                    <div class="list-cell text-md p-sm-t p-sm-b normal text-right">
                                        {{ payment.CreditCard.Mask }} ({{ payment.CreditCard.CardType }})
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </template>

                    <p>{{ $Resources.InvoiceFooterInfo }}</p>
                </div>
            </div>
        </template>
        <template #button>
            <button type="button" class="btn btn-primary" @click="print" :disabled="state.isLoadingOrder">{{ $Resources.PrintReceiptBtn }}</button>
        </template>
    </Modal>
</template>

